import React, { useState } from 'react';
import axios from 'axios';
import ClosedPositionForm from './ClosedPositionForm';
import './RealizedGains.css';
import { API_BASE_URL } from '../lib/api';

function ClosedPositionsTable({ positions, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);

  const handleEdit = (position) => {
    setEditingPosition(position);
    setShowForm(true);
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this closed position?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/transactions/closed-positions/${transactionId}`, {
        withCredentials: true
      });
      onRefresh();
    } catch (err) {
      console.error('Error deleting closed position:', err);
      alert('Failed to delete closed position');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingPosition(null);
    onRefresh();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPosition(null);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="closed-positions-section">
      <div className="section-header">
        <button onClick={() => setShowForm(true)} className="btn-add">
          + Add Closed Position
        </button>
      </div>

      {showForm && (
        <ClosedPositionForm
          position={editingPosition}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {positions.length === 0 ? (
        <div className="empty-state">
          <p>No closed positions for this year.</p>
          <p className="empty-hint">Click "+ Add Closed Position" to record a sale.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="closed-positions-table">
            <thead>
              <tr>
                <th>Date Sold</th>
                <th>Asset</th>
                <th>Quantity</th>
                <th>Sale Price</th>
                <th>Cost Basis</th>
                <th>Realized Gain (EUR)</th>
                <th>Holding Period</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.map(pos => {
                const isAnnual = pos.statementType === 'ANNUAL';
                return (
                  <tr key={pos.transactionId}>
                    <td>{formatDate(pos.date)}</td>
                    <td>
                      <div className="asset-cell">
                        {isAnnual ? (
                          <>
                            <strong>Annual Statement</strong>
                            <br />
                            <span className="ticker">{pos.platform || 'N/A'}</span>
                          </>
                        ) : (
                          <>
                            <strong>{pos.assetName}</strong>
                            <br />
                            <span className="ticker">{pos.ticker}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{isAnnual ? '-' : pos.quantitySold}</td>
                    <td>{isAnnual ? '-' : formatCurrency(pos.salePrice, pos.currency)}</td>
                    <td>{isAnnual ? '-' : formatCurrency(pos.purchasePrice, pos.currency)}</td>
                    <td className={pos.realizedGainEUR >= 0 ? 'gain-positive' : 'gain-negative'}>
                      {formatCurrency(pos.realizedGainEUR, 'EUR')}
                    </td>
                    <td>
                      {isAnnual ? (
                        '-'
                      ) : (
                        <>
                          {pos.holdingPeriodDays} days
                          <br />
                          <span className="holding-period-date">
                            ({formatDate(pos.dateAcquired)})
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {isAnnual ? (
                        '-'
                      ) : (
                        <span className={`tax-badge ${pos.isShortTerm ? 'short-term' : 'long-term'}`}>
                          {pos.isShortTerm ? 'Short-term' : 'Long-term'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(pos)}
                          className="btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(pos.transactionId)}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {positions.length > 0 && (
        <div className="table-summary">
          <p>Total: {positions.length} closed position{positions.length !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  );
}

export default ClosedPositionsTable;
